/// <amd-module name="WorkItemsHub/Utils/WorkItemsHubDeleteHelper" />
import * as React from "react";
import { ActionsCreator } from "Actions/ActionsCreator";
import { IWorkItemsHubFilterDataSource } from "Stores/IWorkItemsHubFilterDataSource";
import { CoreFieldRefNames } from "Wit/Common/Generated/Constants";
import { IVssLayoutManager } from "VSS/Platform/Layout";
import { IVssPageContext } from "VSS/Platform/Context";
import { LegacyComponent } from "VSS/Legacy/Legacy";
import { IVssContributionService } from "VSS/Platform/Contribution";
import { ProjectPermission } from "Wit/Common/Generated/Contracts";
import { WorkItemTypeCategory } from "Wit/Clients/Wit/Contracts/WorkItemTracking";
import { IWorkItemTrackingRestClient, WorkItemTrackingClientName } from "Wit/Clients/Wit/RestClient/WorkItemTracking";
import { ITfsPageService } from "Tfs/Platform/Page";
import { localeIgnoreCaseComparer } from "VSS/Core/Util/String";
import { INamedFieldDataDictionary } from "Wit/WorkItemDataManager/Contracts";
import { DeleteDialog } from "Wit/Common/WorkItemDialogs/Components/DeleteDialog";
import { isFeatureEnabled } from "VSS/Platform/Feature";

interface IWorkItemPermissionData {
    workItemDeletePermission: ProjectPermission;
    workItemDestroyPermission: ProjectPermission;
}

export class WorkItemsHubDeleteHelper {
    private _testWorkItemTypesMap: { [key: string]: string } = null;
    private _hasDeletePermission: boolean = null;

    private testCategories = [
        "Microsoft.TestCaseCategory",
        "Microsoft.TestPlanCategory",
        "Microsoft.TestSuiteCategory",
        "Microsoft.SharedParameterCategory",
        "Microsoft.SharedStepCategory"
    ];

    constructor(private _pageContext: IVssPageContext, private _actionCreator: ActionsCreator) {
        // Don't fetch test work item types unless user has delete permission
        // Note that it calls MVC endpoint (_api/teamprojects) which is not accessible for anonymous/public users
        if (this.hasDeletePermission()) {
            // Queue a request to get test work item types
            this._beginPopulateTestWorkItemTypes();
        }
    }

    /**
     * Checks whether the delete command should be shown for the current selection or not.
     * Following factors impact this choice:
     * 1. User should have WorkItemDelete permissions
     * 2. User should have selected at least 1 work item
     * 3. None of the selected work items should be of types belonging to Test Categories
     * The test artifacts do not support soft delete and thus we don't support deleting those from WorkItems Hub
     * @param dataSource Data source for the current tab
     * @param selectedWorkItemIds Indicates whether it was invoked using keyboard shortcut or not, used for telemetry
     */
    public shouldShowDeleteCommand(dataSource: IWorkItemsHubFilterDataSource, selectedWorkItemIds: number[]): boolean {
        // In the unlikely event of failing to fetch the test work item types, we would never show the delete command.
        // This should still be better than accidentally letting users delete test artifacts permanently.
        if (this.hasDeletePermission() && this._testWorkItemTypesMap != null && selectedWorkItemIds != null && selectedWorkItemIds.length > 0) {
            for (const workItemId of selectedWorkItemIds) {
                const type = dataSource.getValue(workItemId, CoreFieldRefNames.WorkItemType) as string;
                if (this._testWorkItemTypesMap.hasOwnProperty(type)) {
                    return false;
                }
            }
            return true;
        }

        return false;
    }

    /**
     * Deletes the selected work items.
     * @param tabId ID of the current tab
     * @param selectedWorkItemIds Indicates whether it was invoked using keyboard shortcut or not, used for telemetry
     */
    public deleteWorkItems(pageContext: IVssPageContext, tabId: string, selectedWorkItemIds: number[]): void {
        if (selectedWorkItemIds == null || selectedWorkItemIds.length === 0) {
            return;
        }

        const deleteItemsAction = () => {
            // Optimistically remove the deleted workitems from the view
            this._actionCreator.removeWorkItemFromDataProvider(tabId, selectedWorkItemIds);
        };
        const layoutManager = pageContext.getService<IVssLayoutManager>("IVssLayoutManager");
        if (isFeatureEnabled(this._pageContext, "ms.vss-work-web.new-boards-hub-feature", false)) {
            layoutManager.renderCallout(onDismiss => {
                return React.createElement(
                    DeleteDialog,
                    {
                        onDismiss: onDismiss,
                        selectedItems: this.getSelectedWorkItems(selectedWorkItemIds),
                        onDeleteItems: deleteItemsAction
                    },
                    null
                );
            });
        } else {
            layoutManager.renderCallout(() => {
                return React.createElement(
                    LegacyComponent,
                    {
                        modules: ["WorkItemsHub/Scripts/LegacyPlatform/DeleteConfirmationDialog"],
                        wrappedType: "deleteConfirmationDialog",
                        workItemIds: selectedWorkItemIds,
                        onDelete: deleteItemsAction
                    },
                    null
                );
            });
        }
    }

    private _beginPopulateTestWorkItemTypes(): void {
        const testWorkItemTypeDict: { [key: string]: string } = {};
        const tfsPageService = this._pageContext.getService<ITfsPageService>("ITfsPageService");
        const tfsPageData = tfsPageService.getData();
        const projectId = tfsPageData.project.id;

        const client: IWorkItemTrackingRestClient = this._pageContext.getRestClient<IWorkItemTrackingRestClient>(WorkItemTrackingClientName);
        client.getWorkItemTypeCategories(projectId).then((allCategories: WorkItemTypeCategory[]) => {
            if (allCategories) {
                this.testCategories.forEach(categoryName => {
                    const matchedCategories = allCategories.filter(category => {
                        return localeIgnoreCaseComparer(category.referenceName, categoryName) === 0;
                    });
                    matchedCategories.forEach(testCategory => {
                        testCategory.workItemTypes.forEach(name => {
                            testWorkItemTypeDict[name.name] = categoryName;
                        });
                    });
                });
                this._testWorkItemTypesMap = testWorkItemTypeDict;
            }
        });
    }

    public hasDeletePermission(): boolean {
        if (this._hasDeletePermission == null) {
            const contributionService = this._pageContext.getService<IVssContributionService>("IVssContributionService");
            const permissions = contributionService.getData<IWorkItemPermissionData>("ms.vss-work-web.work-item-permissions-data-provider");
            if (permissions && permissions.workItemDeletePermission) {
                this._hasDeletePermission = permissions.workItemDeletePermission.hasPermission;
            }
        }

        return !!this._hasDeletePermission;
    }

    private getSelectedWorkItems(workItemIds: number[]): INamedFieldDataDictionary[] {
        if (workItemIds == null || workItemIds.length === 0) {
            return [];
        }
        return workItemIds.map(workItemId => ({ [CoreFieldRefNames.Id]: workItemId }));
    }
}
